<template>
  <div v-show="!preloading">
    <WdsDrawer
      v-model:show="preload"
      position="right"
      width="365px"
      title="Filters"
      closable
      class="!tw-bg-true-gray-50 tw-overflow-auto"
    >
      <WdsStack direction-column justify-space-between class="tw-flex-1">
        <WdsStack direction-column gap-md>
          <WdsText
            class="tw-border-b-gray-300 tw-border-solid tw-border-b tw-mt-4 tw-pb-1"
            tertiary
            :text="$t('System filters')"
          />
          <div v-for="arg of dynamicArgs" :key="arg.filter.name">
            <template v-if="arg.filter.type !== FilterTypes.COMPONENT">
              <!--     Fully dynamic arg.filters     -->
              <KeepAlive v-if="arg.filter.type === FilterTypes.SELECT">
                <WdsSelect
                  v-model="activeFilterValues[arg.filter.name]"
                  :list="arg.filter.list"
                  :placeholder="$t(arg.filter.placeholder)"
                  :label="fieldToReadAble(arg.filter.title)"
                  :multi-select="arg.filter.multiSelect"
                />
              </KeepAlive>
              <KeepAlive v-if="arg.filter.type === FilterTypes.TOGGLE">
                <WdsToggle
                  v-model:checked="activeFilterValues[arg.filter.name]"
                  :label="$t(fieldToReadAble(arg.filter.title))"
                />
              </KeepAlive>

              <KeepAlive v-if="arg.filter.type === FilterTypes.SINGLE_STRING">
                <WdsInput
                  v-model="activeFilterValues[arg.filter.name]"
                  :label="$t(fieldToReadAble(arg.filter.title))"
                />
              </KeepAlive>
            </template>
            <template v-else>
              <KeepAlive>
                <component
                  :is="arg.filter.component"
                  v-model="activeFilterValues[arg.filter.name]"
                  :meta="arg"
                />
              </KeepAlive>
            </template>
          </div>
        </WdsStack>
        <WdsStack justify-end class="tw-w-full">
          <WdsButton
            class="!tw-mt-4 !tw-mb-2"
            :disabled="!displayResetFiltersButton"
            data-test="tc-filter-reset"
            primary
            @click="resetFilters"
          >
            <div class="tw-my-auto">
              {{ $t('Clear all') }}
            </div>
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </WdsDrawer>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsButton,
  WdsDrawer,
  WdsInput,
  WdsSelect,
  WdsStack,
  WdsText,
  WdsToggle,
} from '@worksome/wds-components'
import { computed, ComputedRef, ref, watch, watchEffect } from 'vue'
import { watchOnce } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  convertArgTypeToTypeStr,
  fieldToReadAble,
  getFieldTypeLeaf,
} from '@/components/GraphqlDataTable/tools'
import { useIntrospectionTypes, useQueryType } from '@/components/GraphqlDataTable/composables'
import LookupFilters, {
  FilterMetaDataProp,
  FilterTypes,
} from '@/components/GraphqlDataTable/filters'
import useQueryEncoder from '@/composables/useQueryEncoder'
import { InputValueFragmentDef, TypeRefFragmentDef } from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'

const emit = defineEmits<{
  numberOfFilters: [num: number]
}>()

interface TableFilterProps {
  query: string
  displayAllArgs?: boolean
  filterBlackList?: string[]
}

const show = defineModel<boolean>('show')

// Preloading logic start
// This is a preloading experiment that allows the filters to get initialized and setup before they are shown.
// It reduces initial load for the filters and allows the filters to set the default filter values.
// This feature should be moved into wds-drawer imo, via a preload prop that makes the component use v-show instead of
// v-if, since that allows the filters to mount and prepare
// The main downside to this method is that the first load animation is broken and it will increase table load, but that
// could potentially be circumnavigated somehow with timeouts. We could also result in a few extra page loads for the
// table since multiple filters could be making requests and emmit variable updates, which is a potential risk
const preloading = ref(true)
const preload = computed({
  get() {
    return preloading.value || show.value
  },
  set(val) {
    show.value = val
  },
})

watchOnce(show, () => {
  preloading.value = false
})
// Preloading logic end

const props = withDefaults(defineProps<TableFilterProps>(), {
  displayAllArgs: false,
  filterBlackList: () => [],
})

const reactiveQuery = computed(() => props.query)
const activeFilterValues = defineModel<object>('filters', { default: () => ({}) })

const resetFilters = () => {
  for (const key in activeFilterValues.value) {
    if (key in activeFilterValues.value) {
      if (activeFilterValues.value[key] instanceof Array) {
        activeFilterValues.value[key] = []
      } else if (typeof activeFilterValues.value[key] === 'object') {
        for (const subKey in activeFilterValues.value[key]) {
          if (subKey in activeFilterValues.value[key]) {
            activeFilterValues.value[key][subKey] = null
          }
        }
      } else {
        activeFilterValues.value[key] = undefined
      }
    }
  }
}

const displayResetFiltersButton = computed(
  () =>
    Object.values(activeFilterValues.value).reduce((acc, val) => {
      if (val instanceof Array) {
        return acc + (val.length > 0 ? 1 : 0)
      }
      if (typeof val === 'object') {
        return acc + (Object.values(val).some((v) => v !== null) ? 1 : 0)
      }

      return !!val + acc
    }, 0) > 0,
)

const { typeLookup } = useIntrospectionTypes()

const enumTypeToSelector = ({ leaf, arg, listType: multiSelect }) => {
  const { name, enumValues, description } = typeLookup.value[leaf.name]
  return {
    list:
      enumValues?.map((value) => ({ ...value, label: value.description, value: value.name })) ?? [],
    title: name,
    name: arg.name,
    description: arg.description,
    placeholder: description,
    multiSelect,
    type: FilterTypes.SELECT,
  }
}

const scalarTypeToSelector = ({ leaf, arg, listType }) => {
  switch (leaf.name) {
    case 'Boolean':
      return {
        type: FilterTypes.TOGGLE,
        name: arg.name,
        title: arg.name,
        description: arg.description,
      }
    case 'String':
      if (listType) return null
      // Do stuff here
      return {
        type: FilterTypes.SINGLE_STRING,
        name: arg.name,
        title: arg.name,
        description: arg.description,
      }
    default:
      return null
  }
}

const containsListType = (field) => {
  let { type } = field
  // Unwrap the type until there is no more 'ofType'
  do {
    const { ofType, kind } = type
    if (kind === 'LIST') return true
    type = ofType
  } while (type)

  return false
}

const blackList = computed(() => ['first', 'page', ...props.filterBlackList])

const { type: queryType, args: queryArgs } = useQueryType(reactiveQuery)

const argToSpecificKey = (arg) => `${arg.name}-${convertArgTypeToTypeStr(arg.type)}`
const argToGenericKey = (arg) => `${convertArgTypeToTypeStr(arg.type)}`

const dynamicArgs: ComputedRef<FilterMetaDataProp[]> = computed(() => {
  const selected = queryType.value
  if (!selected) return []

  const hardCodedArgs = blackList.value
  const inputtedArgNames = queryArgs.value.inputtedArgs.map(({ name }) => name)
  const ENUM_TYPE = 'ENUM'
  const SCALAR_TYPE = 'SCALAR'
  // Build the arguments string for the query
  const args = selected.args
    .map((arg) => useFragment(InputValueFragmentDef, arg))
    .filter((arg) => {
      const argName = arg.name
      return (
        !hardCodedArgs.includes(argName) &&
        (props.displayAllArgs || inputtedArgNames.includes(argName))
      )
    })
    .map((arg) => ({
      arg,
      leaf: getFieldTypeLeaf(useFragment(TypeRefFragmentDef, arg.type)),
      listType: containsListType(arg),
    }))

  // We sort the args to align as best we can with the inputtedArgs to allow control over the ordering
  args.sort(
    ({ arg: argA }, { arg: argB }) =>
      inputtedArgNames.findIndex((name) => argA.name === name) -
      inputtedArgNames.findIndex((name) => argB.name === name),
  )

  return (
    args
      .map((arg) => {
        const argKeySpecific = argToSpecificKey(arg.arg)
        const argKeyGeneric = argToGenericKey(arg.arg)
        if (LookupFilters[argKeySpecific]) {
          return {
            arg,
            dynamicArg: {
              type: FilterTypes.COMPONENT,
              component: LookupFilters[argKeySpecific],
              name: arg.arg.name,
              description: arg.arg.description,
            },
          }
        }
        if (LookupFilters[argKeyGeneric]) {
          return {
            arg,
            dynamicArg: {
              type: FilterTypes.COMPONENT,
              component: LookupFilters[argKeyGeneric],
              name: arg.arg.name,
              description: arg.arg.description,
            },
          }
        }

        switch (arg.leaf.kind) {
          case SCALAR_TYPE:
            return { arg, dynamicArg: scalarTypeToSelector(arg) }
          case ENUM_TYPE:
            return { arg, dynamicArg: enumTypeToSelector(arg) }
        }
        return { arg, dynamicArg: null }
      })
      .filter(({ arg, dynamicArg }) => !!dynamicArg)
      .map(({ arg, dynamicArg }) => ({
        filter: dynamicArg as NonNullable<typeof dynamicArg>,
        graphqlData: arg,
      }))
      // Here we enforce the type since we have filtered out the nullable options in our filter
      .map((filter) => filter as unknown as FilterMetaDataProp)
  )
})

watchEffect(() => emit('numberOfFilters', dynamicArgs.value.length))

const buildEncoding = (name, value) =>
  useQueryEncoder(
    name,
    value,
    (raw, _) => (raw !== '' ? JSON.parse(atob(raw)) : value),
    (data, _) => (data ? btoa(JSON.stringify(data)) : ''),
  )

watch(dynamicArgs, () => {
  for (const { filter } of dynamicArgs.value) {
    activeFilterValues.value[filter.name] = buildEncoding(filter.name, undefined)
  }
})
</script>

<style lang="scss" scoped>
.field-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    max-height: 300px;
    overflow: auto;
  }
}

.query-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    overflow: auto;
  }
}
</style>
